import React, { useEffect, useMemo, useRef, useCallback, memo } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Portal,
  Icon,
  Link,
  Text,
  chakra,
  useOutsideClick,
  usePopper,
} from "@chakra-ui/react";
import { BiLink, BiUnlink, BiWorld } from "react-icons/bi";
import { AnimatePresence, motion } from "framer-motion";
import { translate as trans } from "../Trans/Trans";

const wrapperStyles = {
  position: "absolute",
  zIndex: "popover",
  display: "flex",
  alignItems: "center",
  background: "gray.500",
  "--popper-arrow-bg": "var(--chakra-colors-gray-500)",
  boxShadow: "md",
  borderRadius: "md",
  borderWidth: "1px",
  paddingTop: "1",
  paddingBottom: "1",
  paddingLeft: "2",
  paddingRight: "2",
  maxWidth: "full",
};

const StyledContent = chakra(motion.div);

const AdvancedMessageLinkMenu = ({
  node,
  text,
  range,
  onExit,
  onToggleLinkDisabling,
  getLinkData,
}) => {
  const ref = useRef(null);
  const { popperRef, referenceRef, getArrowProps, getArrowInnerProps } =
    usePopper({
      placement: "top",
      strategy: "fixed",
    });
  const active = useMemo(() => !!(node && text && range), [node, text, range]);
  const linkData = useMemo(
    () => getLinkData?.(range, text),
    [text, range, getLinkData]
  );

  const setRef = (el) => {
    ref.current = el;
    popperRef(el);
  };

  const handleToggleLinkDisabling = useCallback(() => {
    onToggleLinkDisabling?.(range, text);
  }, [text, range, onToggleLinkDisabling]);

  useOutsideClick({
    ref,
    handler: (event) => {
      if (event?.target !== node && !node?.contains?.(event.target)) {
        onExit?.();
      }
    },
  });

  useEffect(() => {
    referenceRef(node);
  }, [node]);

  if (!active || !linkData?.url) {
    return null;
  }

  return (
    <AnimatePresence>
      <Portal>
        <StyledContent sx={wrapperStyles} ref={setRef}>
          <Box display="flex" alignItems="center" marginRight="1">
            <Tooltip placement="top" hasArrow label={linkData.url}>
              <Button
                as={Link}
                variant="ghost"
                size="xs"
                leftIcon={<Icon as={BiWorld} />}
                href={linkData.url}
                isExternal
              >
                <Text isTruncated maxWidth="150px">
                  {linkData.url}
                </Text>
              </Button>
            </Tooltip>
          </Box>
          {onToggleLinkDisabling ? ( // @NOTE to disable option for toggling shortening for links without short link add: && linkData.short
            <Tooltip
              placement="top"
              hasArrow
              label={
                linkData.disabled
                  ? trans("post_editor.shortlink.enable_shortening")
                  : trans("post_editor.shortlink.disable_shortening")
              }
            >
              <IconButton
                onClick={handleToggleLinkDisabling}
                icon={
                  <Icon
                    as={linkData.disabled ? BiLink : BiUnlink}
                    fontSize="md"
                  />
                }
                size="xs"
                variant="ghost"
              />
            </Tooltip>
          ) : null}
          <Box {...getArrowProps()}>
            <Box {...getArrowInnerProps()} />
          </Box>
        </StyledContent>
      </Portal>
    </AnimatePresence>
  );
};

AdvancedMessageLinkMenu.propTypes = {
  node: PropTypes.instanceOf(Node),
  text: PropTypes.string,
  range: PropTypes.shape({
    from: PropTypes.number,
    to: PropTypes.number,
  }),
  onExit: PropTypes.func,
  onToggleLinkDisabling: PropTypes.func,
  getLinkData: PropTypes.func,
};

AdvancedMessageLinkMenu.defaultProps = {
  node: undefined,
  text: undefined,
  range: undefined,
  onExit: undefined,
  onToggleLinkDisabling: undefined,
  getLinkData: undefined,
};

export default memo(AdvancedMessageLinkMenu);
